import './styles/main.css';
import { NeuralNetworkViz } from './lib/three-setup';
import {
  pageLoadAnimation,
  initScrollAnimations,
  headerScrollAnimation,
  buttonHoverEffects,
  cardHoverEffects,
  stepsAnimation,
  roadmapAnimation,
  glitchText,
  initInteractiveEffects
} from './lib/animations';
import { CosmicBackground } from './lib/cosmic-bg';
import {
  getGlobalStats,
  getHolderRewardsData,
  type HolderRewardsData,
  type GlobalRewardsStats
} from './lib/contract';

// Global state
let cosmicBg: CosmicBackground | null = null;
let neuralNetworkViz: NeuralNetworkViz | null = null;
let currentAddress: string | null = null;

// Initialize application
async function init(): Promise<void> {
  // Hide loading screen after a short delay
  const loadingScreen = document.getElementById('loading-screen');

  try {
    // Initialize cosmic background (scroll-reactive)
    cosmicBg = new CosmicBackground('cosmic-bg-container');

    // Initialize header scroll behavior
    const header = document.getElementById('header');
    if (header) {
      headerScrollAnimation(header);
    }

    // Initialize mobile menu
    initMobileMenu();

    // Initialize chart toggle
    initChartToggle();

    // Initialize calculator toggle
    initCalculatorToggle();

    // Initialize address lookup
    initAddressLookup();

    // Initialize copy address functionality
    initCopyAddress();

    // Initialize FAQ accordion
    initFaqAccordion();

    // Initialize smooth scrolling
    initSmoothScrolling();

    // Run page load animation
    pageLoadAnimation();

    // Initialize scroll-triggered animations
    initScrollAnimations();

    // Initialize hover effects
    buttonHoverEffects();
    cardHoverEffects();

    // Initialize interactive effects (cursor, magnetic buttons, ripples)
    initInteractiveEffects();

    // Initialize section-specific animations
    const howItWorksSection = document.getElementById('how-it-works');
    if (howItWorksSection) {
      stepsAnimation(howItWorksSection);
    }

    const roadmapSection = document.getElementById('roadmap');
    if (roadmapSection) {
      roadmapAnimation(roadmapSection);
    }

    // Initialize glitch effects on logo
    const logoElements = document.querySelectorAll('.glitch-text');
    logoElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        glitchText(el);
      }
    });

    // Initialize neural network visualization when technology section is in view
    initNeuralNetworkOnScroll();

    // Load global stats from blockchain
    loadGlobalStats();

    // Start periodic refresh of global stats
    startGlobalStatsRefresh();

  } catch (error) {
    console.error('Initialization error:', error);
  } finally {
    // Hide loading screen
    if (loadingScreen) {
      setTimeout(() => {
        loadingScreen.classList.add('hidden');
      }, 500);
    }
  }
}

// Mobile menu functionality
function initMobileMenu(): void {
  const menuBtn = document.getElementById('mobile-menu-btn');
  const mobileMenu = document.getElementById('mobile-menu');

  if (menuBtn && mobileMenu) {
    menuBtn.addEventListener('click', () => {
      mobileMenu.classList.toggle('hidden');
    });

    // Close menu when clicking a link
    mobileMenu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        mobileMenu.classList.add('hidden');
      });
    });
  }
}

// Chart slide-in panel functionality
function initChartToggle(): void {
  const toggleBtn = document.getElementById('chart-toggle-btn');
  const chartPanel = document.getElementById('chart-panel');

  if (toggleBtn && chartPanel) {
    toggleBtn.addEventListener('click', () => {
      const isOpen = chartPanel.classList.contains('open');

      if (isOpen) {
        chartPanel.classList.remove('open');
        toggleBtn.classList.remove('active');
      } else {
        chartPanel.classList.add('open');
        toggleBtn.classList.add('active');
      }
    });

    // Close panel when pressing Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && chartPanel.classList.contains('open')) {
        chartPanel.classList.remove('open');
        toggleBtn.classList.remove('active');
      }
    });
  }
}

// Calculator slide-in panel functionality
function initCalculatorToggle(): void {
  const toggleBtn = document.getElementById('calculator-toggle-btn');
  const calcPanel = document.getElementById('calculator-panel');

  if (toggleBtn && calcPanel) {
    toggleBtn.addEventListener('click', () => {
      const isOpen = calcPanel.classList.contains('open');

      if (isOpen) {
        calcPanel.classList.remove('open');
        toggleBtn.classList.remove('active');
      } else {
        calcPanel.classList.add('open');
        toggleBtn.classList.add('active');
      }
    });

    // Close panel when pressing Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && calcPanel.classList.contains('open')) {
        calcPanel.classList.remove('open');
        toggleBtn.classList.remove('active');
      }
    });
  }
}


// Copy address functionality
function initCopyAddress(): void {
  const copyBtn = document.getElementById('copy-address-btn');
  const addressElement = document.getElementById('contract-address');

  if (copyBtn && addressElement) {
    copyBtn.addEventListener('click', async () => {
      const address = addressElement.textContent || '';

      try {
        await navigator.clipboard.writeText(address);

        // Show success state
        copyBtn.classList.add('copied');

        // Reset after 2 seconds
        setTimeout(() => {
          copyBtn.classList.remove('copied');
        }, 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = address;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        // Show success state
        copyBtn.classList.add('copied');
        setTimeout(() => {
          copyBtn.classList.remove('copied');
        }, 2000);
      }
    });
  }
}

// FAQ accordion functionality
function initFaqAccordion(): void {
  const faqItems = document.querySelectorAll('.faq-item');

  faqItems.forEach((item) => {
    const toggle = item.querySelector('.faq-toggle');
    const content = item.querySelector('.faq-content');
    const icon = toggle?.querySelector('svg');

    if (toggle && content && icon) {
      toggle.addEventListener('click', () => {
        const isOpen = !content.classList.contains('hidden');

        // Close all other items
        faqItems.forEach((otherItem) => {
          const otherContent = otherItem.querySelector('.faq-content');
          const otherIcon = otherItem.querySelector('.faq-toggle svg');
          if (otherContent && otherItem !== item) {
            otherContent.classList.add('hidden');
            otherIcon?.classList.remove('rotate-180');
          }
        });

        // Toggle current item
        if (isOpen) {
          content.classList.add('hidden');
          icon.classList.remove('rotate-180');
        } else {
          content.classList.remove('hidden');
          icon.classList.add('rotate-180');
        }
      });
    }
  });
}

// Initialize address lookup functionality
function initAddressLookup(): void {
  const addressInput = document.getElementById('wallet-address-input') as HTMLInputElement;
  const lookupBtn = document.getElementById('lookup-btn');

  if (lookupBtn) {
    lookupBtn.addEventListener('click', handleAddressLookup);
  }

  if (addressInput) {
    // Handle Enter key
    addressInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        handleAddressLookup();
      }
    });

    // Auto-lookup when pasting a valid address
    addressInput.addEventListener('paste', () => {
      // Small delay to get the pasted value
      setTimeout(() => {
        const value = addressInput.value.trim();
        if (isValidAddress(value)) {
          handleAddressLookup();
        }
      }, 100);
    });
  }
}

// Validate Ethereum address format
function isValidAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

// Handle address lookup
async function handleAddressLookup(): Promise<void> {
  const addressInput = document.getElementById('wallet-address-input') as HTMLInputElement;
  const lookupBtn = document.getElementById('lookup-btn');
  const realRewardsSection = document.getElementById('real-rewards-section');

  if (!addressInput) return;

  const address = addressInput.value.trim();

  // Validate address
  if (!isValidAddress(address)) {
    // Show error feedback
    addressInput.style.borderColor = '#FF6464';
    setTimeout(() => {
      const wrapper = addressInput.parentElement;
      if (wrapper) wrapper.style.borderColor = '';
    }, 2000);
    return;
  }

  // Show loading state
  if (lookupBtn) {
    lookupBtn.classList.add('loading');
    (lookupBtn as HTMLButtonElement).disabled = true;
  }

  // Show rewards section
  realRewardsSection?.classList.remove('hidden');

  try {
    currentAddress = address;
    await loadHolderRewards(address);
  } catch (error) {
    console.error('Error looking up address:', error);
  } finally {
    // Reset button state
    if (lookupBtn) {
      lookupBtn.classList.remove('loading');
      (lookupBtn as HTMLButtonElement).disabled = false;
    }
  }
}

// Load and display global stats from blockchain
async function loadGlobalStats(): Promise<void> {
  try {
    const stats: GlobalRewardsStats = await getGlobalStats();

    // Dividend Info
    setElementText('global-total-distributed', `${stats.totalDividendsDistributedFormatted} BTC`);
    setElementText('global-holders', stats.numberOfHolders.toLocaleString());
    setElementText('global-claim-wait', formatSeconds(Number(stats.claimWaitSeconds)));
    setElementText('global-last-processed', stats.lastProcessedIndex.toLocaleString());

    // Token Info
    setElementText('global-total-supply', stats.totalSupplyFormatted);
    setElementText('global-contract-balance', stats.contractTokenBalanceFormatted);

    // Fees
    setElementText('global-buy-fees', `${stats.totalBuyFees}%`);
    setElementText('global-sell-fees', `${stats.totalSellFees}%`);

    // Contract State
    setElementText('global-gas-processing', stats.gasForProcessing.toLocaleString());
    setElementText('global-swap-threshold', stats.swapTokensAtAmountFormatted);
    setElementText('global-burning', stats.burningEnabled ? 'Enabled' : 'Disabled');
    setElementText('global-liq-release', stats.liquidityRelease20Percent ? 'Yes' : 'No');

    // Addresses with BSCScan links
    setAddressLink('global-dividends-token', stats.dividendsToken);
    setAddressLink('global-dividend-tracker', stats.dividendTracker);
    setAddressLink('global-lp-pair', stats.uniswapV2Pair);
    setAddressLink('global-owner', stats.owner);

  } catch (error) {
    console.error('Error loading global stats:', error);
    setElementText('global-total-distributed', 'Error loading');
    setElementText('global-holders', '--');
  }
}

// Helper to set element text
function setElementText(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

// Helper to set address link
function setAddressLink(id: string, address: string): void {
  const el = document.getElementById(id) as HTMLAnchorElement;
  if (el && address) {
    el.textContent = `${address.slice(0, 6)}...${address.slice(-4)}`;
    el.href = `https://bscscan.com/address/${address}`;
    el.title = address;
  }
}

// Format seconds to human readable
function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

// Load and display holder rewards from blockchain
async function loadHolderRewards(address: string): Promise<void> {
  const balanceEl = document.getElementById('real-balance');
  const claimableEl = document.getElementById('real-claimable');
  const totalEarnedEl = document.getElementById('real-total-earned');
  const shareEl = document.getElementById('real-share');

  // Show loading state
  [balanceEl, claimableEl, totalEarnedEl, shareEl].forEach(el => {
    if (el) el.textContent = 'Loading...';
  });

  try {
    const rewards: HolderRewardsData = await getHolderRewardsData(address);

    // Update real rewards display
    if (balanceEl) {
      balanceEl.textContent = `${rewards.tokenBalanceFormatted} BITCULT`;
    }

    if (claimableEl) {
      claimableEl.textContent = `${rewards.withdrawableDividendsFormatted} BTC`;
    }

    if (totalEarnedEl) {
      totalEarnedEl.textContent = `${rewards.totalDividendsEarnedFormatted} BTC`;
    }

    if (shareEl) {
      shareEl.textContent = `${rewards.sharePercentage.toFixed(4)}%`;
    }

  } catch (error) {
    console.error('Error loading holder rewards:', error);

    // Show error state
    [balanceEl, claimableEl, totalEarnedEl, shareEl].forEach(el => {
      if (el) el.textContent = 'Error';
    });
  }
}

// Refresh global stats periodically (every 60 seconds)
let globalStatsRefreshInterval: ReturnType<typeof setInterval> | null = null;

function startGlobalStatsRefresh(): void {
  if (globalStatsRefreshInterval) return;

  globalStatsRefreshInterval = setInterval(() => {
    loadGlobalStats();
    // Also refresh holder rewards if an address is loaded
    if (currentAddress) {
      loadHolderRewards(currentAddress);
    }
  }, 60000); // Refresh every 60 seconds
}

function stopGlobalStatsRefresh(): void {
  if (globalStatsRefreshInterval) {
    clearInterval(globalStatsRefreshInterval);
    globalStatsRefreshInterval = null;
  }
}

// Smooth scrolling for anchor links
function initSmoothScrolling(): void {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      e.preventDefault();
      const href = anchor.getAttribute('href');
      if (href) {
        const target = document.querySelector(href);
        if (target) {
          const headerOffset = 80;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    });
  });
}

// Initialize neural network visualization when section comes into view
function initNeuralNetworkOnScroll(): void {
  const techSection = document.getElementById('technology');
  const container = document.getElementById('neural-network-container');

  if (techSection && container) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !neuralNetworkViz) {
            // Clear placeholder content
            container.innerHTML = '';
            // Initialize 3D visualization
            neuralNetworkViz = new NeuralNetworkViz('neural-network-container');
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(techSection);
  }
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (cosmicBg) {
    cosmicBg.dispose();
  }
  if (neuralNetworkViz) {
    neuralNetworkViz.dispose();
  }
  stopGlobalStatsRefresh();
});

// Start the application
document.addEventListener('DOMContentLoaded', init);
